import { useQuery } from "@tanstack/react-query";
import { useReadContract } from "wagmi";
import { ONE_SECOND_MS } from "../utils/time";
import { QUERY_KEYS } from "./constants";
import { usePortalApiClient } from "./usePortalApiClient";
import { priceOracleContract } from "../lib/contracts";
import { fromFloatToBigInt } from "../utils/numbers";
import { getCryptoAsset } from "../utils/cryptoAssets";
import { CHAIN_ID } from "../constants";
import { usePassportContext } from "./usePassportContext";
const BTC_TARGET_DIGITS = getCryptoAsset("BTC").decimals;
const T_TARGET_DIGITS = getCryptoAsset("mT").decimals;
// If the oracle has not been updated in at least 60 seconds, it is stale.
const MAX_PRICE_DELAY = 60 * ONE_SECOND_MS;
/**
 * Scales the price by the number of digits.
 * @dev Oracles returns prices with variable number of digits. This function
 *      scales the price to a fixed number of digits ensuring consistency.
 * @param price - The price to scale.
 * @param priceDigits - The number of digits in the price.
 * @returns The scaled price.
 */
function scalePriceByDigits(price, priceDigits) {
    if (priceDigits > BTC_TARGET_DIGITS) {
        return price / 10n ** BigInt(priceDigits - BTC_TARGET_DIGITS);
    }
    if (priceDigits < BTC_TARGET_DIGITS) {
        return price * 10n ** BigInt(BTC_TARGET_DIGITS - priceDigits);
    }
    return price;
}
/**
 * Hook to fetch assets conversion rates.
 * @see https://github.com/mezo-org/musd/blob/f0b2030315f0d8b0fc11a9fc778856fe4673051f/solidity/contracts/PriceFeed.sol
 * @returns {}.data.{}.price - The latest asset price.
 * @returns {}.data.{}.digits - The latest asset price digits.
 * @returns {}.isPending - Whether the request is pending.
 * @returns {}.isError - Whether there was an error fetching the price.
 */
export function useAssetsConversionRates() {
    const portalApiClient = usePortalApiClient();
    const { environment = "mainnet" } = usePassportContext();
    const btcPrice = useReadContract({
        ...priceOracleContract,
        functionName: "latestRoundData",
        query: {
            refetchInterval: ({ state }) => {
                if (!state.data)
                    return MAX_PRICE_DELAY;
                const [, , , updatedAt] = state.data ?? [];
                if (!updatedAt)
                    return MAX_PRICE_DELAY;
                const age = Date.now() - Number(updatedAt) * 1000;
                return age > MAX_PRICE_DELAY
                    ? // If the data is stale, we want to refetch as soon as possible, but
                        // not faster than once per second, to avoid excessive requests.
                        // Math.max ensures the interval is at least 1 second.
                        Math.max(ONE_SECOND_MS, MAX_PRICE_DELAY - age)
                    : MAX_PRICE_DELAY - age;
            },
            select: (data) => {
                const [, answer] = data;
                return scalePriceByDigits(answer, BTC_TARGET_DIGITS);
            },
        },
        chainId: CHAIN_ID[environment],
    });
    const tPrice = useQuery({
        queryKey: [QUERY_KEYS.ASSETS_USD_CONVERSION],
        queryFn: () => portalApiClient.getPortalStatistics(),
        select: (data) => fromFloatToBigInt(data.currentUsdPerTToken, T_TARGET_DIGITS),
        refetchInterval: MAX_PRICE_DELAY,
        throwOnError: true,
    });
    return {
        data: {
            t: {
                price: tPrice.data ?? 0n,
                decimals: T_TARGET_DIGITS,
            },
            btc: {
                price: btcPrice.data ?? 0n,
                decimals: BTC_TARGET_DIGITS,
            },
        },
        isPending: tPrice.isPending || btcPrice.isPending,
        isError: tPrice.isError || btcPrice.isError,
    };
}
//# sourceMappingURL=useAssetsConversionRates.js.map